import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../Utils/toastContext';
import { debounce } from 'lodash';

export default function TrainingModule({ module, onBack }) {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const { showToast } = useToast();
  const [answers, setAnswers] = useState({});
  const [moduleData, setModuleData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rawQuestions, setRawQuestions] = useState([]);
  const isCompleted = moduleData?.completion_status === 'completed';
  const nextSlide = () =>
    currentSlide < slides.length - 1 && setCurrentSlide((prev) => prev + 1);
  const prevSlide = () =>
    currentSlide > 0 && setCurrentSlide((prev) => prev - 1);

  const handleAnswer = (qid, option) => {
    if (isCompleted) return; // ❌ block changes
    setAnswers({ ...answers, [qid]: option });
  };

  const correctCount = questions.filter(
    (q) => answers[q.id] === q.answer,
  ).length;

  const total = questions.length;
  const progress = ((currentSlide + 1) / slides.length) * 100;
  const score = Math.round((correctCount / total) * 100);
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const getModuleData = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/training-modules/${module.id}`,
      );

      const moduleRes = response.data.module;
      const questionsRes = response.data.questions || [];

      setModuleData(moduleRes);
      setRawQuestions(response.data.questions);

      // ✅ MAP API → UI FORMAT
      const mappedQuestions = questionsRes.map((q) => {
        const correctOption = q.options.find(
          (opt) => opt.id === q.correct_answer,
        );

        return {
          id: q.id,
          question: q.question_text,
          options: q.options.map((opt) => opt.value),
          answer: correctOption?.value, // 🔥 important for correctness check
        };
      });

      setQuestions(mappedQuestions);
      // ✅ AUTO-FILL ANSWERS IF COMPLETED
      if (moduleRes.completion_status === 'completed') {
        const prefilledAnswers = {};

        questionsRes.forEach((q) => {
          const correctOption = q.options.find(
            (opt) => opt.id === q.correct_answer,
          );

          if (q.type?.value === 'checkbox') {
            prefilledAnswers[q.id] = q.options
              .filter((opt) => q.correct_answer.includes(opt.id))
              .map((opt) => opt.value);
          } else {
            prefilledAnswers[q.id] = correctOption?.value;
          }
        });

        setAnswers(prefilledAnswers);
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const getSlides = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/training-modules/${module.id}/pages`,
      );

      if (response.status == 200) {
        setSlides(response.data.pages);
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const buildSubmitPayload = () => {
    return {
      answers: rawQuestions.map((q) => {
        const selectedValue = answers[q.id];

        // RADIO / SINGLE SELECT
        if (q.type?.value === 'radio') {
          const selectedOption = q.options.find(
            (opt) => opt.value === selectedValue,
          );

          return {
            question_id: q.id,
            answer: selectedOption?.id,
          };
        }

        // MULTI SELECT
        if (q.type?.value === 'checkbox') {
          const selectedIds = q.options
            .filter((opt) => selectedValue?.includes(opt.value))
            .map((opt) => opt.id);

          return {
            question_id: q.id,
            answer: selectedIds,
          };
        }

        // TEXT INPUT
        return {
          question_id: q.id,
          answer: selectedValue,
        };
      }),
    };
  };
  const postSubmitAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const apiUrl = `${BASE_URL}/api/v1/training-modules/${module.id}/complete`;
    const payload = buildSubmitPayload();
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));

        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleSubmitAnswer = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await postSubmitAPI();
        if (response.status === 200) {
          toastMessage = { message: response.message, type: 'success' };
          onBack();
        }
      } catch (error) {
        toastMessage = { message: error.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000),
    [questions, module, answers], // Dependencies
  );
  useEffect(() => {
    getModuleData();
    getSlides();
  }, [module]);
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 p-4 md:p-6">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {/* HEADER */}
      <div className="max-w-6xl mx-auto mb-6 flex justify-between items-center">
        <button
          onClick={onBack}
          className="text-sm text-gray-600 hover:text-black transition"
        >
          ← Back
        </button>
        <h1 className="text-lg md:text-xl font-semibold text-gray-800">
          Compliance Training
        </h1>
        <span className="text-xs text-gray-500">
          Progress {Math.round(progress)}%
        </span>
      </div>

      {/* SLIDER */}
      <div className="max-w-6xl mx-auto bg-white dark:bg-gray-950 rounded-3xl shadow-md p-4 md:p-6">
        {/* Progress */}
        <div className="w-full h-2 bg-gray-100 dark:bg-gray-800 rounded-full mb-4 overflow-hidden">
          <div
            className="h-2 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* SLIDER WRAPPER (IMPORTANT FIX) */}
        <div className="relative overflow-hidden rounded-2xl bg-gray-50 dark:bg-gray-900 h-[260px] md:h-[450px] flex items-center justify-center">
          <AnimatePresence mode="sync">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.02 }}
              transition={{ duration: 0.35, ease: 'easeInOut' }}
              className="absolute inset-0 flex items-center justify-center p-4 md:p-6"
            >
              <img
                src={slides[currentSlide]?.image_url}
                alt="slide"
                className="
            max-h-[200px] md:max-h-[420px]
            w-auto max-w-full
            object-contain
            rounded-xl
            shadow-sm
          "
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* CONTROLS */}
        <div className="flex justify-between mt-5">
          <button
            onClick={prevSlide}
            className="px-4 py-2 text-xs md:text-sm rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition flex items-center gap-2"
          >
            ← Previous
          </button>

          <button
            onClick={nextSlide}
            className="px-4 py-2 text-xs md:text-sm rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:opacity-90 transition flex items-center gap-2"
          >
            Next →
          </button>
        </div>
      </div>

      {/* QUIZ */}
      <div className="max-w-6xl mx-auto mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: QUESTIONS */}
        <div className="lg:col-span-2 space-y-4">
          {questions.map((q) => {
            return (
              <motion.div
                key={q.id}
                whileHover={{ scale: 1.01 }}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
              >
                <p className="text-sm md:text-base font-medium text-gray-800 mb-3">
                  {q.question}
                </p>

                <div className="space-y-2">
                  {q.options.map((opt) => {
                    const selected = answers[q.id];
                    const isCorrectAnswer = opt === q.answer;
                    const isSelected = selected === opt;
                    const isCorrect = isCompleted
                      ? isCorrectAnswer
                      : isSelected && selected === q.answer;

                    const isWrong =
                      !isCompleted && isSelected && selected !== q.answer;

                    return (
                      <motion.label
                        whileTap={{ scale: 0.98 }}
                        key={opt}
                        className={`relative flex items-center justify-between gap-3 p-4 rounded-2xl cursor-pointer border transition-all duration-300
        ${
          isSelected && isCorrect
            ? 'border-green-500 bg-green-50 shadow-[0_0_0_2px_rgba(34,197,94,0.25)]'
            : ''
        }
        ${
          isSelected && isWrong
            ? 'border-red-500 bg-red-50 shadow-[0_0_0_2px_rgba(239,68,68,0.25)]'
            : ''
        }
        ${
          !isSelected
            ? 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
            : ''
        }
      `}
                      >
                        {/* LEFT SIDE */}
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            checked={isSelected}
                            disabled={isCompleted}
                            onChange={() => handleAnswer(q.id, opt)}
                            className="accent-indigo-600 w-6 h-4"
                          />
                          <span className="text-sm text-gray-800 font-medium">
                            {opt}
                          </span>
                        </div>

                        {/* RIGHT SIDE STATUS */}
                        {isSelected && (
                          <div className="flex items-center gap-2">
                            <span
                              className={`material-symbols-outlined text-xl 
              ${isCorrect ? 'text-green-600' : 'text-red-600'}
            `}
                            >
                              {isCorrect ? 'verified' : 'dangerous'}
                            </span>

                            <span
                              className={`text-[11px] px-2 py-1 rounded-full font-semibold
              ${
                isCorrect
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }
            `}
                            >
                              {isCorrect ? 'Correct' : 'Incorrect'}
                            </span>
                          </div>
                        )}

                        {/* LEFT BORDER ACCENT STRIP */}
                        {isSelected && (
                          <div
                            className={`absolute left-0 top-0 h-full w-1 rounded-l-2xl
            ${isCorrect ? 'bg-green-500' : 'bg-red-500'}
          `}
                          />
                        )}
                      </motion.label>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* RIGHT: STATS PANEL */}
        <div className="bg-white rounded-2xl shadow-sm p-4 h-fit sticky top-4">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Your Progress
          </h3>

          {/* Score */}
          <div className="mb-4">
            <div className="text-3xl font-bold text-indigo-600">{score}%</div>
            <p className="text-xs text-gray-500">Current Score</p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="bg-green-50 text-green-600 p-2 rounded-lg">
              {correctCount}
              <div>Correct</div>
            </div>
            <div className="bg-red-50 text-red-600 p-2 rounded-lg">
              {Object.keys(answers).length - correctCount}
              <div>Wrong</div>
            </div>
            <div className="bg-gray-100 text-gray-600 p-2 rounded-lg">
              {total - Object.keys(answers).length}
              <div>Left</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="w-full h-2 bg-gray-100 rounded-full">
              <div
                className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                style={{ width: `${score}%` }}
              />
            </div>
          </div>

          {/* Submit */}
          {score === 100 && !isCompleted && (
            <button
              onClick={(e) => {
                e.preventDefault();
                setLoading(true);
                handleSubmitAnswer(e);
              }}
              className="mt-4 w-full py-2 rounded-xl bg-green-500 text-white text-sm hover:bg-green-600 transition"
            >
              Submit ✔
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
